import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { DefaultDateOfBirth, PatientRoles, Role } from "../config/DiactiveTypes";
import { determineDevicePlatform } from "../utils/DiactiveUtils";
import { getAuthUserId } from "./AuthService";
import { createAuthAccount, deleteAuthAccount, updateAuthAccount } from "./FunctionService";
import { firestore } from "./FirebaseService";

const usersCollectionName = "users";
const usersCollection = collection(firestore, usersCollectionName);

const trimString = (v) => (typeof v === 'string' ? v.trim() : v);

// Role-specific extensions to the generic CRUD flow below.
// Each entry is optional; missing hooks fall back to the generic behavior.
//   - normalizeFields(payload): returns the per-role extra fields to persist
//     on create/update (in addition to firstName/lastName/email).
//   - extraReturnFields(rawData): returns the per-role extra fields to
//     include in rows returned by getAllUsersByRole.
//   - getDeleteBlockReason(userId): returns a string reason if the user
//     should not be deleted, or null to allow the delete.
const RoleExtensions = {
  [Role.AppTester]: {
    normalizeFields: ({ polarRequired, cgmType, insulinPumpType, usesCgm }) => ({
      hasAppAccess: true,
      canTrain: true,
      bypassDailyWorkout: true,
      polarRequired,
      cgmType: cgmType ?? '',
      insulinPumpType: insulinPumpType ?? '',
      usesCgm: usesCgm ?? false,
      fitnessLevel: 0,
      progression: 0,
    }),
    extraReturnFields: (data) => ({
      polarRequired: data?.polarRequired ?? false,
      cgmType: data?.cgmType ?? '',
      insulinPumpType: data?.insulinPumpType ?? '',
      usesCgm: data?.usesCgm ?? false,
    }),
  },
  [Role.Doctor]: {
    normalizeFields: ({ hospital }) => ({ hospital: trimString(hospital) }),
    extraReturnFields: (data) => ({ hospital: data?.hospital ?? null }),
    getDeleteBlockReason: async (userId) => {
      const patientsQuery = query(usersCollection, where('doctor', '==', userId));
      const patientsSnap = await getDocs(patientsQuery);
      const hasPatient = patientsSnap.docs.some((d) => PatientRoles.includes(d.data().role));
      if (hasPatient) {
        return 'No se puede eliminar el médico porque tiene pacientes asignados.';
      }

      /*
      // TODO Disabled for now because there's no doctor field in the evaluations documents,
      // but there will be once refactor is done, i.e. createdBy field is added to the
      // evaluations documents.
      const evalQuery = query(collectionGroup(firestore, evalCollectionName), where('doctor', '==', userId));
      const evalSnap = await getDocs(evalQuery);
      if (!evalSnap.empty) {
        return 'No se puede eliminar el médico porque está asociado a evaluaciones de pacientes.';
      }
      */

      return null;
    },
  },
};

const CrudAllowedRoles = [Role.SuperUser, Role.Doctor, Role.AppTester];

const assertCrudAllowed = (role) => {
  if (!CrudAllowedRoles.includes(role)) {
    throw new Error(`CRUD not supported for role: ${role}`);
  }
};

export const getUserFullName = async (userId) => {
  const docRef = doc(firestore, usersCollectionName, userId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const userData = docSnap.data();
    return `${userData.firstName} ${userData.lastName}`;
  }
  return null;
};

export const getUserByEmail = async (email) => {
  if (!email) return null;
  const q = query(usersCollection, where("email", "==", email));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;

  const docSnap = querySnapshot.docs[0];
  const data = docSnap.data();
  const fullName = data ? `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim() : null;

  return {
    id: docSnap.id,
    email: data?.email ?? null,
    firstName: data?.firstName ?? null,
    lastName: data?.lastName ?? null,
    nickname: data?.nickname ?? null,
    role: data?.role ?? null,
    fullName: fullName || null
  };
};

export const getUserById = async (userId) => {
  if (!userId) return null;
  const docRef = doc(firestore, usersCollectionName, userId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  const fullName = data ? `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim() : null;
  const createdBy = data?.createdBy ?? null;
  const createdByName = createdBy ? await getUserFullName(createdBy) : null;

  return {
    id: docSnap.id,
    email: data?.email ?? null,
    firstName: data?.firstName ?? null,
    lastName: data?.lastName ?? null,
    hospital: data?.hospital ?? null,
    polarRequired: data?.polarRequired ?? false,
    cgmType: data?.cgmType ?? '',
    insulinPumpType: data?.insulinPumpType ?? '',
    usesCgm: data?.usesCgm ?? false,
    role: data?.role ?? null,
    createdAt: data?.createdAt ?? null,
    createdBy,
    createdByName,
    fullName: fullName || null
  };
};

export const getPatientBasicInfo = async (userId) => {
  if (!userId) return null;
  const docRef = doc(firestore, usersCollectionName, userId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  const fullName = `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim();
  return {
    id: docSnap.id,
    fullName: fullName || null,
    nickname: data?.nickname ?? null,
    role: data?.role ?? null,
    hospital: data?.hospital ?? null,
    patientNumber: data?.patientNumber ?? null,
  };
};

export const getUserDevices = async (userId) => {
  const q = query(
    collection(firestore, "devices"),
    where("userId", "==", userId),
    orderBy("timestamp", "desc")
  );
  const querySnapshot = await getDocs(q);

  const devices = {
    models: [],
    platforms: [],
  };
  querySnapshot.docs.forEach((doc) => {
    const deviceData = doc.data();
    devices.models.push(deviceData.deviceModel);
    const devicePlatform = determineDevicePlatform(deviceData.deviceOS);
    devices.platforms.push(devicePlatform);
  });
  return devices;
};

export const getUserEmail = async (userId) => {
  if (!userId) return null;
  const docRef = doc(firestore, usersCollectionName, userId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data()?.email ?? null : null;
};

export const addUser = async (role, payload) => {
  assertCrudAllowed(role);

  const { firstName, lastName, email } = payload;
  const fn = firstName.trim();
  const ln = lastName.trim();
  const em = email.toLowerCase().trim();
  const extraFields = RoleExtensions[role]?.normalizeFields?.(payload) ?? {};

  try {
    const userId = await createAuthAccount(em, fn, role);
    const createdBy = getAuthUserId();
    const data = {
      firstName: fn,
      lastName: ln,
      email: em,
      role,
      dateOfBirth: Timestamp.fromDate(new Date(DefaultDateOfBirth)),
      hasAppAccess: false,
      canTrain: false,
      ...extraFields,
      ...(createdBy ? { createdBy } : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const userDocRef = doc(firestore, usersCollectionName, userId);
    await setDoc(userDocRef, data);
    return userId;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const updateUser = async (role, userId, payload) => {
  assertCrudAllowed(role);

  const { firstName, lastName, email } = payload;
  const fn = firstName.trim();
  const ln = lastName.trim();
  const em = email.toLowerCase().trim();
  const extraFields = RoleExtensions[role]?.normalizeFields?.(payload) ?? {};

  try {
    await updateAuthAccount(userId, em, fn, role);

    const userDocRef = doc(firestore, usersCollectionName, userId);
    await updateDoc(userDocRef, {
      firstName: fn,
      lastName: ln,
      email: em,
      ...extraFields,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const deleteUser = async (role, userId) => {
  assertCrudAllowed(role);

  const blockReason = await RoleExtensions[role]?.getDeleteBlockReason?.(userId);
  if (blockReason) {
    throw new Error(blockReason);
  }

  try {
    await deleteAuthAccount(userId);
    await deleteDoc(doc(firestore, usersCollectionName, userId));
    return true;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const getAllUsersByRole = async (role) => {
  assertCrudAllowed(role);

  const extraReturnFields = RoleExtensions[role]?.extraReturnFields;

  const q = query(
    usersCollection,
    where("role", "==", role),
    orderBy("createdAt")
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    const firstName = data?.firstName ?? null;
    const lastName = data?.lastName ?? null;
    const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();

    return {
      id: docSnap.id,
      firstName,
      lastName,
      fullName: fullName || null,
      email: data?.email ?? null,
      createdAt: data?.createdAt ?? null,
      role: data?.role ?? null,
      ...(extraReturnFields?.(data) ?? {}),
    };
  });
};
